import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Calendar, ChevronRight, Clock } from 'lucide-react';

const GROUP_META = {
  overdue: {
    icon: AlertTriangle,
    tone: 'bg-red-500/15 border-red-400/30 text-red-300',
    buttonClass:
      'inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-red-500 hover:bg-red-400 text-white transition-colors',
  },
  week: {
    icon: Calendar,
    tone: 'bg-blue-500/15 border-blue-400/30 text-blue-300',
    buttonClass:
      'inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors',
  },
  upcoming: {
    icon: Clock,
    tone: 'bg-violet-500/15 border-violet-400/30 text-violet-300',
    buttonClass:
      'inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors',
  },
};

function PaymentRow({ item, onTreat, primary = false, treatLabel }) {
  const meta = GROUP_META[item.timing] || GROUP_META.upcoming;
  const Icon = meta.icon;

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-5 sm:px-6 py-4 border-b border-white/[0.06] last:border-b-0">
      <div className="flex items-start gap-4 min-w-0 flex-1">
        <div
          className={`shrink-0 h-11 w-11 rounded-full border flex items-center justify-center ${meta.tone}`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="m-0 text-sm sm:text-[0.95rem] font-semibold text-white">{item.title}</p>
          <p className="m-0 mt-1 text-xs sm:text-sm text-white/45">{item.subtitle}</p>
        </div>
      </div>

      <div className="shrink-0 pl-[3.75rem] sm:pl-0">
        <button
          type="button"
          onClick={() => onTreat?.(item)}
          className={primary ? GROUP_META.overdue.buttonClass : meta.buttonClass}
        >
          {treatLabel}
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function AIAgentPaymentsPanel({
  groups = [],
  isLoading = false,
  onTreatPayment,
  showWhenEmpty = false,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.paymentsPanel.${key}`, opts);
  const totalItems = groups.reduce((sum, group) => sum + group.items.length, 0);

  if (isLoading) {
    return (
      <section className="mx-auto w-full max-w-4xl mb-4">
        <div className="rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.08] via-blue-950/20 to-indigo-950/30 backdrop-blur-lg overflow-hidden animate-pulse">
          <div className="px-5 sm:px-6 py-4 border-b border-white/10">
            <div className="h-5 w-52 bg-white/10 rounded-lg" />
          </div>
          <div className="px-5 sm:px-6 py-8 space-y-4">
            <div className="h-12 bg-white/5 rounded-2xl" />
            <div className="h-12 bg-white/5 rounded-2xl" />
          </div>
        </div>
      </section>
    );
  }

  if (!totalItems && !showWhenEmpty) return null;

  return (
    <section className="mx-auto w-full max-w-4xl mb-4">
      <div className="rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.08] via-blue-950/20 to-indigo-950/30 backdrop-blur-lg overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <Calendar className="h-5 w-5 text-white/70 shrink-0" />
          <h2 className="m-0 text-base sm:text-lg font-semibold text-white">{p('title')}</h2>
        </div>

        {totalItems === 0 ? (
          <p className="m-0 px-5 sm:px-6 py-8 text-sm text-white/45 text-center">{p('empty')}</p>
        ) : (
          groups.map((group) => (
            <div key={group.id}>
              <div className="px-5 sm:px-6 py-2.5 bg-white/[0.03] border-b border-white/[0.06]">
                <p className="m-0 text-xs font-semibold uppercase tracking-wide text-white/40">
                  {group.label}
                </p>
              </div>
              {group.items.map((item) => (
                <PaymentRow
                  key={item.id}
                  item={item}
                  onTreat={onTreatPayment}
                  primary={item.timing === 'overdue'}
                  treatLabel={p('treat')}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
